/**
 * 공통 레이아웃
 * ---------------------------------------------------------------------------
 * 메뉴는 권한·구독에 따라 감춘다. 다시 말하지만 UX 이지 보안이 아니다.
 *
 * 메뉴가 하나도 없을 때
 *   그냥 비워 두면 안 된다. 구독에 모듈이 빠져 있으면 메뉴가 통째로 사라지는데,
 *   화면에는 아무 설명이 없어 "갑자기 메뉴가 안 보인다" 로만 보인다.
 *   실제로 그 상태가 된 적이 있다. 왜 비었는지, 무엇을 하면 되는지 말해 준다.
 *
 * 지원 모드 표시
 *   플랫폼 운영자가 고객사 데이터를 보고 있을 때는 화면 전체에 띠를 둔다.
 *   자기 회사 데이터를 보고 있다고 착각한 채 조작하는 사고를 막는다.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Layout, Menu, Result, Space, Tag, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  HomeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ECO_MODULES, ECO_PERMISSIONS, LOG_TYPES, LOG_TYPE_MENU } from '@eco/shared';
import { LOG_TYPE_ICON } from './logTypeVisual';
import { can, type EcoSession } from '../lib/session';
import { api } from '../lib/api';
import { signOut } from '../lib/auth';
import { AppFooter } from './AppFooter';

const { Header, Content } = Layout;

/**
 * 기업 로고를 받아 둔다.
 *
 * `<img src="/api/company/logo">` 로 둘 수 없다 — 토큰이 요청 헤더로 가는데
 * <img> 는 헤더를 붙이지 못한다. blob 으로 받아 붙인다.
 *
 * 등록하지 않았으면 서버가 204 를 준다. 오류가 아니다 — 그때는 제품 로고만
 * 보여 준다.
 */
function useCompanyLogo(etag: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!etag) {
      setUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      return;
    }

    let cancelled = false;
    let created: string | null = null;

    void (async () => {
      try {
        const res = await api.get('/company/logo', { responseType: 'blob' });
        const blob = res.data as Blob | undefined;
        if (cancelled) return;
        created = blob && blob.size > 0 ? URL.createObjectURL(blob) : null;
        setUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return created;
        });
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // 지문이 바뀌면 다시 받는다. 기업이 로고를 바꾼 경우다.
  }, [etag]);

  return url;
}

/**
 * 무료 기간이 이만큼 남았을 때부터 화면에 띠를 둔다.
 *
 * 메일 공지는 별도다(플랫폼의 TRIAL_NOTICE 공통코드가 D-7/3/1 을 정한다).
 * 화면은 매일 보는 곳이라 좀 더 넉넉히 두어도 방해가 되지 않는다.
 */
const TRIAL_WARN_DAYS = 14;

/** antd 의 메뉴 항목 형을 그대로 쓴다. 직접 정의하면 하위 메뉴가 생길 때 어긋난다. */
type MenuItem = NonNullable<MenuProps['items']>[number] & { key: string };

export function AppLayout({ session, children }: { session: EcoSession; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const companyLogo = useCompanyLogo(session.companyLogo?.etag ?? null);

  const items: MenuItem[] = [];

  const hasDailyLog = session.modules.includes(ECO_MODULES.DAILY_LOG);
  const hasFacility = session.modules.includes(ECO_MODULES.FACILITY);
  const hasReport = session.modules.includes(ECO_MODULES.REPORT);

  // 대시보드가 맨 앞이고 홈 화면이다. 들어오자마자 빠진 날을 본다.
  if (hasDailyLog && can(session, ECO_PERMISSIONS.DAILY_LOG_READ)) {
    items.push({ key: '/dashboard', icon: <DashboardOutlined />, label: '대시보드' });
  }

  // 세 서식을 각각 메뉴에 둔다.
  //
  // 예전에는 목록 안의 드롭다운으로 골랐다. 사용자는 한 번에 한 종류의
  // 일지만 다루는데, 화면을 열 때마다 유형을 다시 고르게 되고 무엇을 보고
  // 있었는지도 주소에 남지 않았다. 메뉴로 나누면 즐겨찾기도 된다.
  if (hasDailyLog && can(session, ECO_PERMISSIONS.DAILY_LOG_READ)) {
    for (const type of LOG_TYPES) {
      items.push({
        key: `/logs/${type}`,
        icon: LOG_TYPE_ICON[type],
        label: LOG_TYPE_MENU[type],
      });
    }
  }
  if (hasFacility && can(session, ECO_PERMISSIONS.SITE_READ)) {
    items.push({ key: '/sites', icon: <HomeOutlined />, label: '사업장·시설' });
  }
  if (hasReport && can(session, ECO_PERMISSIONS.SELF_MEASURE_READ)) {
    items.push({
      key: '/self-measurements',
      icon: <FileProtectOutlined />,
      label: '자가측정 서류관리',
    });
  }

  /*
   * AI 보고서 보관함.
   *
   * 보고서는 일지를 요약한 것이라, 일지를 볼 수 있으면 그 요약도 볼 수
   * 있어야 한다 — 권한을 따로 두지 않는다.
   */
  if (hasDailyLog && can(session, ECO_PERMISSIONS.DAILY_LOG_READ)) {
    items.push({ key: '/ai-reports', icon: <FileTextOutlined />, label: 'AI 보고서' });
  }

  /*
   * 내 정보는 **권한·구독과 무관하게** 늘 있다.
   *
   * WORK&Hub 콘솔이 기업 관리자 전용이 되면서(2026-10-27), 일반
   * 사용자가 비밀번호를 바꿀 수 있는 자리가 여기뿐이다. 모듈을
   * 구독하지 않았거나 권한을 못 받은 사람일수록 더 필요하다 —
   * 그런 사람이 가장 먼저 하는 일이 "내 계정이 맞나" 를 보는 것이다.
   */
  items.push({ key: '/me', icon: <UserOutlined />, label: '내 정보' });

  // 메뉴가 빈 이유는 둘 중 하나다. 원인마다 할 일이 다르므로 나눠서 알린다.
  //   구독에 모듈이 없다  → 관리자가 플랫폼에서 요금제를 확인해야 한다
  //   권한이 없다         → 기업 관리자가 역할을 줘야 한다
  const noModules = !hasDailyLog && !hasFacility && !hasReport;
  // 「내 정보」는 늘 있으므로 그것만 남았을 때를 막힌 것으로 본다.
  const blocked = items.every((i) => i.key === '/me');

  // 세션을 아직 못 받았으면 열린 것으로 본다. 진짜 판정은 서버 가드가 한다.
  const access = session.access ?? {
    readable: true,
    writable: true,
    reason: null,
    message: null,
    trialEndsAt: null,
    trialDaysLeft: null,
  };
  // 결제 수단 등록은 플랫폼의 일이다. 제품에 결제 화면을 두지 않는다.
  const billingUrl = `${session.platformWebUrl}/app/billing`;

  /*
   * 머리글의 무료 기간 딱지.
   *
   * `trialDaysLeft` 는 **체험 중일 때만** 숫자다. 유료로 넘어갔거나 플랫폼이
   * 그 값을 안 주는 버전이면 null 이고, 그때는 아무것도 그리지 않는다 —
   * 모르는 것을 「무료 아님」으로 단정하지 않는다.
   *
   * 색은 급한 정도만 알린다. 문구에 날짜가 이미 들어 있어 색을 못 봐도 읽힌다.
   */
  const days = access.trialDaysLeft;
  const trialLabel =
    days === null || days === undefined
      ? null
      : days === 0
        ? '무료 사용 · 오늘 종료'
        : `무료 사용 · ${days}일 남음`;
  const trialTone = days === null || days === undefined
    ? 'blue'
    : days <= 3 ? 'red' : days <= 7 ? 'orange' : 'blue';

  // 현재 경로로 선택 항목을 정한다. 하위 경로(/sites/:id/facilities)도 묶는다.
  const selected = items
    .map((i) => i.key)
    .filter((key) => location.pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)
    .slice(0, 1);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {session.supportMode && (
        <div
          style={{
            background: '#d4380d',
            color: '#fff',
            textAlign: 'center',
            padding: '6px 12px',
            fontWeight: 600,
          }}
        >
          지원 모드 — 고객사 데이터를 보고 있습니다. 모든 조회·변경이 기록됩니다.
        </div>
      )}

      <Header style={{ display: 'flex', alignItems: 'center', gap: 24, paddingInline: 24 }}>
        {/*
          CI. 로고 이미지가 받아지지 않아도 제품 이름은 남아야 하므로 alt 를
          제품명으로 둔다. 헤더가 어두운 배경이라 높이만 맞추고 여백은 둔다.
        */}
        <img
          src="/workneco-logo.png"
          alt="WORK&ECO"
          height={30}
          style={{ display: 'block', flexShrink: 0 }}
        />

        <Menu
          theme="dark"
          mode="horizontal"
          items={items}
          selectedKeys={selected}
          onClick={({ key }) => navigate(key)}
          style={{ flex: 1, minWidth: 0 }}
        />

        <Space>
          {/*
            무료 사용 기간 딱지.

            아래 띠(TRIAL_WARN_DAYS)와 역할이 다르다. 저쪽은 **끝나 가니
            조치하라**는 경고라 마지막 며칠에만 나오고, 이것은 **지금 무료로
            쓰고 있다**는 상태 표시라 기간 내내 떠 있다. 둘 다 없으면 고객은
            어느 날 갑자기 막히고 나서야 체험이었다는 것을 안다.

            남은 날짜는 플랫폼이 세 준다(access.trialDaysLeft). 제품이 세면
            표준시간대와 경계 처리가 갈려 Hub 화면·안내 메일과 하루씩
            어긋난다.
          */}
          {trialLabel && (
            <Tag color={trialTone} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
              {trialLabel}
            </Tag>
          )}
          {/*
            기업 로고는 **회사 이름 옆**에 둔다. 왼쪽의 제품 로고는 그대로
            남는다 — 왼쪽은 "지금 쓰는 제품", 오른쪽은 "지금 보는 회사" 다.
            등록하지 않았으면 아무것도 그리지 않는다.
          */}
          {companyLogo && (
            <img
              src={companyLogo}
              alt={session.companyName ?? '기업 로고'}
              style={{ display: 'block', height: 26, maxWidth: 150, objectFit: 'contain' }}
            />
          )}
          {session.companyName && (
            <Typography.Text style={{ color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap' }}>
              {session.companyName}
            </Typography.Text>
          )}
          {session.displayName && <Tag color="green">{session.displayName}</Tag>}
          <Typography.Link href={session.platformWebUrl} style={{ color: '#b7eb8f' }}>
            플랫폼으로
          </Typography.Link>
          {/* 제품에서만 나간다. 플랫폼 세션은 그대로 둔다. */}
          <Typography.Link
            onClick={() => void signOut()}
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            나가기
          </Typography.Link>
        </Space>
      </Header>

      <Content style={{ padding: 24 }}>
        {/*
          구독 상태 경고.

          두 가지를 구분한다.
            막힌 상태  등록·수정이 안 된다. 왜인지와 무엇을 하면 되는지 알린다
            남은 기간  아직 되지만 곧 막힌다. 미리 알린다

          조회는 막지 않으므로 화면 자체는 그대로 둔다 — 운영일지는 법정 기록이라
          이미 쓴 것을 못 보게 하면 고객이 법정 의무를 이행할 수 없다.

          닫을 수 있게 두지 않는다. 저장이 안 되는 이유를 모르는 채로 쓰다가
          "저장 버튼이 고장났다" 는 문의가 되는 것이 더 나쁘다.
        */}
        {!access.writable && access.message && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="등록·수정이 제한되었습니다"
            description={access.message}
            action={
              <Button size="small" type="primary" href={billingUrl} target="_blank">
                결제 수단 등록
              </Button>
            }
          />
        )}

        {access.writable && access.trialDaysLeft !== null && access.trialDaysLeft <= TRIAL_WARN_DAYS && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              access.trialDaysLeft === 0
                ? '무료 사용 기간이 오늘 끝납니다'
                : `무료 사용 기간이 ${access.trialDaysLeft}일 남았습니다`
            }
            description="기간이 끝나면 조회는 계속 가능하지만 새로 등록하거나 수정할 수 없습니다."
            action={
              <Button size="small" href={billingUrl} target="_blank">
                결제 수단 등록
              </Button>
            }
          />
        )}

        {blocked ? (
          <Result
            status="warning"
            title={noModules ? '이 제품을 이용할 수 있는 요금제가 아닙니다' : '접근 권한이 없습니다'}
            subTitle={
              noModules
                ? '구독은 되어 있지만 요금제에 WORK&ECO 기능이 포함되어 있지 않습니다.'
                : '이 제품을 쓸 수 있는 권한이 계정에 부여되지 않았습니다.'
            }
            extra={
              <Space direction="vertical" size={12} style={{ width: '100%', maxWidth: 520 }}>
                <Alert
                  type="info"
                  showIcon
                  message={
                    noModules
                      ? '기업 관리자가 플랫폼의 [제품] 화면에서 요금제를 확인해 주세요.'
                      : '기업 관리자에게 WORK&ECO 권한을 요청해 주세요.'
                  }
                />
                <Button type="primary" href={session.platformWebUrl} target="_blank">
                  플랫폼으로 이동
                </Button>
              </Space>
            }
          />
        ) : (
          children
        )}
      </Content>

      <AppFooter />
    </Layout>
  );
}
